import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import './ProviderAccountPage.css';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { useToast } from '@/components/common/toast';
import { Select2 } from '@/components/forms/Select2';
import { Select2Multi } from '@/components/forms/Select2Multi';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { usePermission } from '@/permissions/usePermission';
import { ApiError } from '@/api/client/abpError';
import {
  AccountType,
  ConditionalFee,
  type ProviderAccountDto,
  type ProviderAccountFormDto,
  type ProviderAccountLookupDto,
} from '@/api/types/providers';
import { FeeInputs, emptyFeeBlock, toNumber } from './FeeInputs';
import type { FeeBlock } from './FeeInputs';
import { AccountLogsGrids } from './AccountLogsGrids';
import { ProviderPermissions } from './permissions';
import { useAccountLookup, useCreateAccount, useProviderAccount, useUpdateAccount } from './api';

const OWNERSHIP_OPTIONS = ['N/A', 'Corporate', 'Clients'];

interface AccountFormState {
  startingBalanceDate: string;
  startingBalance: number;
  currencyId: number;
  holdingReserves: number;
  rollingDays: number;
  providerStatementStartingDate: string;
  providerTimeZone: string;
  minMatchPercentage: number;
  ownership: string;
  accountType: number;
  dateSimilarityPercentage: number;
  amountSimilarityPercentage: number;
  providerNameSimilarityPercentage: number;
  countrySimilarityPercentage: number;
  transactionPlusXDays: number;
  linkedProviderAccountIds: string[];
  processDayMinus1: boolean;
  excludeFromProviderAssets: boolean;
  includeStatementsInProviderAssets: boolean;
  feesCondition: ConditionalFee;
  minThreshold: string;
  maxThreshold: string;
  feesWithoutConditions: FeeBlock;
  lessThanFees: FeeBlock;
  betweenFees: FeeBlock;
  greaterThanFees: FeeBlock;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoDateOnly(value: string | null | undefined): string {
  if (!value) return '';
  return value.slice(0, 10);
}

function numericOrNull(raw: string): number | null {
  const trimmed = raw.replace(/,/g, '').trim();
  if (trimmed === '') return null;
  const v = parseFloat(trimmed);
  return Number.isNaN(v) ? null : v;
}

function feeBlockFrom(fee: ProviderAccountDto['feesWithoutConditions'] | undefined): FeeBlock {
  if (!fee) return emptyFeeBlock();
  return {
    depositFixedFee: fee.depositFixedFee,
    withdrawalFixedFee: fee.withdrawalFixedFee,
    depositPercentageFee: fee.depositPercentageFee,
    withdrawalPercentageFee: fee.withdrawalPercentageFee,
  };
}

function initialState(account: ProviderAccountDto | null): AccountFormState {
  if (!account) {
    return {
      startingBalanceDate: todayIso(),
      startingBalance: 0,
      currencyId: 0,
      holdingReserves: 0,
      rollingDays: 0,
      providerStatementStartingDate: '',
      providerTimeZone: '',
      minMatchPercentage: 80,
      ownership: '',
      accountType: 0,
      dateSimilarityPercentage: 0,
      amountSimilarityPercentage: 0,
      providerNameSimilarityPercentage: 0,
      countrySimilarityPercentage: 0,
      transactionPlusXDays: 0,
      linkedProviderAccountIds: [],
      processDayMinus1: false,
      excludeFromProviderAssets: false,
      includeStatementsInProviderAssets: false,
      feesCondition: ConditionalFee.WithoutConditions,
      minThreshold: '',
      maxThreshold: '',
      feesWithoutConditions: emptyFeeBlock(),
      lessThanFees: emptyFeeBlock(),
      betweenFees: emptyFeeBlock(),
      greaterThanFees: emptyFeeBlock(),
    };
  }

  const min = account.lessThanFees?.threshold;
  const max = account.greaterThanFees?.threshold;
  return {
    startingBalanceDate: isoDateOnly(account.startingBalanceDate),
    startingBalance: account.startingBalance,
    currencyId: account.currencyId,
    holdingReserves: account.holdingReserves,
    rollingDays: account.rollingDays,
    providerStatementStartingDate: isoDateOnly(account.providerStatementStartingDate),
    providerTimeZone: account.providerTimeZone ?? '',
    minMatchPercentage: account.minMatchPercentage,
    ownership: account.ownership ?? '',
    accountType: account.accountType,
    dateSimilarityPercentage: account.dateSimilarityPercentage,
    amountSimilarityPercentage: account.amountSimilarityPercentage,
    providerNameSimilarityPercentage: account.providerNameSimilarityPercentage,
    countrySimilarityPercentage: account.countrySimilarityPercentage,
    transactionPlusXDays: account.transactionPlusXDays,
    linkedProviderAccountIds: account.linkedProviderAccountIds ?? [],
    processDayMinus1: account.processDayMinus1,
    excludeFromProviderAssets: account.excludeFromProviderAssets,
    includeStatementsInProviderAssets: account.includeStatementsInProviderAssets,
    feesCondition: account.feesCondition,
    minThreshold: min === null || min === undefined ? '' : String(min),
    maxThreshold: max === null || max === undefined ? '' : String(max),
    feesWithoutConditions: feeBlockFrom(account.feesWithoutConditions),
    lessThanFees: feeBlockFrom(account.lessThanFees),
    betweenFees: feeBlockFrom(account.betweenFees),
    greaterThanFees: feeBlockFrom(account.greaterThanFees),
  };
}

const ACCOUNT_TYPE_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Provider Assets' },
  { value: 1, label: 'Corporate Funds' },
];

const FEES_CONDITION_OPTIONS: { value: number; label: string }[] = [
  { value: ConditionalFee.WithConditions, label: 'Fees with conditions' },
  { value: ConditionalFee.WithoutConditions, label: 'Fees without conditions' },
];

function formatThresholdDisplay(raw: string): string {
  const v = numericOrNull(raw);
  if (v === null) return '';
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

export function ProviderAccountPage() {
  const { providerId = '' } = useParams<{ providerId: string }>();
  const [searchParams] = useSearchParams();
  const providerNameFromQuery = searchParams.get('providerName') ?? '';

  const can = usePermission();
  const canManageAccount = can(ProviderPermissions.manageAccount);

  const navigate = useNavigate();
  const toast = useToast();

  const lookupQuery = useAccountLookup();
  const accountQuery = useProviderAccount(providerId, canManageAccount);
  const createMut = useCreateAccount();
  const updateMut = useUpdateAccount();

  const [form, setForm] = useState<AccountFormState | null>(null);

  const account = accountQuery.data ?? null;
  const isNew = account === null;
  const ready = lookupQuery.isSuccess && accountQuery.isSuccess;

  useEffect(() => {
    if (ready) {
      setForm(initialState(account));
    }
  }, [ready, account]);

  const providerName = account?.providerName || providerNameFromQuery;

  // Mirrors AccountPageModelBase: the current account cannot link to itself.
  const linkableAccounts = useMemo(() => {
    const all = lookupQuery.data?.linkableAccounts ?? [];
    return account ? all.filter((a) => a.id !== account.id) : all;
  }, [lookupQuery.data, account]);

  if (!canManageAccount) {
    return <ErrorState message="You do not have permission to manage provider accounts." />;
  }

  if (lookupQuery.isError || accountQuery.isError) {
    return (
      <ErrorState
        message="Failed to load the provider account."
        onRetry={() => {
          void lookupQuery.refetch();
          void accountQuery.refetch();
        }}
      />
    );
  }

  if (lookupQuery.isLoading || accountQuery.isLoading || !form) return <Spinner />;

  const lookup: ProviderAccountLookupDto = lookupQuery.data!;
  const withConditions = Number(form.feesCondition) === ConditionalFee.WithConditions;

  const patch = (partial: Partial<AccountFormState>) => {
    setForm((prev) => (prev ? { ...prev, ...partial } : prev));
  }

  const minThresholdValue = numericOrNull(form.minThreshold);
  const maxThresholdValue = numericOrNull(form.maxThreshold);
  const thresholdInvalid =
    withConditions &&
    minThresholdValue !== null &&
    maxThresholdValue !== null &&
    minThresholdValue > maxThresholdValue;

  const validate = (): string[] => {
    const errors: string[] = [];
    if (!(form.currencyId > 0)) errors.push('Please select a provider currency.');
    if (!form.startingBalanceDate) errors.push('Please choose a starting balance date.');
    if (!form.ownership.trim()) errors.push('Please select the ownership.');
    if (!form.providerTimeZone.trim()) errors.push('Please select a provider time zone.');

    if (withConditions) {
      if (minThresholdValue === null || maxThresholdValue === null) {
        errors.push('Please enter both the minimum and maximum thresholds for the conditional fees.');
      } else if (maxThresholdValue < minThresholdValue) {
        errors.push('The maximum threshold must be greater than or equal to the minimum threshold.');
      }
    }

    if (!isNew) {
      const simSum =
        form.dateSimilarityPercentage +
        form.amountSimilarityPercentage +
        form.countrySimilarityPercentage +
        form.providerNameSimilarityPercentage;
      if (simSum !== 100) {
        errors.push('The similarity percentages (date, amount, country and provider name) must add up to 100%.');
      }
    }

    return errors;
  }

  const buildPayload = (): ProviderAccountFormDto => {
    return {
      providerId,
      currencyId: form.currencyId,
      startingBalance: form.startingBalance,
      startingBalanceDate: form.startingBalanceDate || null,
      holdingReserves: form.holdingReserves,
      rollingDays: form.rollingDays,
      providerStatementStartingDate: form.providerStatementStartingDate || null,
      providerTimeZone: form.providerTimeZone,
      minMatchPercentage: form.minMatchPercentage,
      ownership: form.ownership,
      accountType: form.accountType as AccountType,
      processDayMinus1: form.processDayMinus1,
      excludeFromProviderAssets: form.excludeFromProviderAssets,
      includeStatementsInProviderAssets: form.includeStatementsInProviderAssets,
      dateSimilarityPercentage: form.dateSimilarityPercentage,
      amountSimilarityPercentage: form.amountSimilarityPercentage,
      countrySimilarityPercentage: form.countrySimilarityPercentage,
      providerNameSimilarityPercentage: form.providerNameSimilarityPercentage,
      feesCondition: Number(form.feesCondition) as ConditionalFee,
      transactionPlusXDays: form.transactionPlusXDays,
      linkedProviderAccountIds: form.linkedProviderAccountIds,
      feesWithoutConditions: { ...form.feesWithoutConditions },
      lessThanFees: { ...form.lessThanFees, threshold: minThresholdValue },
      betweenFees: {
        ...form.betweenFees,
        threshold: minThresholdValue,
        thresholdMaxIfConditionBetween: maxThresholdValue,
      },
      greaterThanFees: { ...form.greaterThanFees, threshold: maxThresholdValue },
    };
  }

  function reportError(err: unknown, fallback: string) {
    toast.error(err instanceof ApiError ? err.message : fallback);
  }

  const handleSave = () => {
    const errors = validate();
    if (errors.length) {
      toast.error(errors.join(' '));
      return;
    }

    const payload = buildPayload();
    if (isNew) {
      createMut.mutate(payload, {
        onSuccess: () => {
          toast.success('Account created successfully');
          navigate('/admin/providers');
        },
        onError: (err) => reportError(err, 'Failed to create the account.'),
      });
    } else {
      updateMut.mutate(
        { id: account!.id, input: payload },
        {
          onSuccess: () => {
            toast.success('Account updated successfully');
            void accountQuery.refetch();
          },
          onError: (err) => reportError(err, 'Failed to update the account.'),
        },
      );
    }
  }

  const saving = createMut.isPending || updateMut.isPending;
  const title = `${isNew ? 'Create' : 'Update'} ${providerName} Account`;

  return (
    <div>
      <PageHeader title={title} breadcrumb={['Home', 'Providers', isNew ? 'Create' : 'Update']} />

      <form className="w-100" onSubmit={(e) => e.preventDefault()}>
        {/* _AccountInfo.cshtml */}
        <div className="card custom-card">
          <div className="card-header border-bottom">
            <h3 className="card-title">{title}</h3>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-lg-3 mb-3 pa-required">
                <DateRangePicker
                  mode="single"
                  label="Starting Balance Date"
                  value={form.startingBalanceDate || null}
                  disabled={!isNew}
                  onChange={(v) => patch({ startingBalanceDate: v })}
                />
              </div>
              <div className="col-lg-3 mb-3 pa-required">
                <label htmlFor="pa-starting-balance" className="form-label">
                  Starting Balance
                </label>
                <input
                  id="pa-starting-balance"
                  type="number"
                  step="any"
                  className="form-control"
                  value={Number.isNaN(form.startingBalance) ? '' : form.startingBalance}
                  disabled={!isNew}
                  onChange={(e) => patch({ startingBalance: toNumber(e.target.value) })}
                />
              </div>
              <div className="col-lg-3 mb-3 pa-required">
                <Select2
                  label="Provider Currency"
                  placeholder="Select Currency"
                  value={form.currencyId || ''}
                  disabled={!isNew}
                  onChange={(v) => patch({ currencyId: v ? Number(v) : 0 })}
                  options={lookup.currencies.map((c) => ({ value: c.id, label: c.name }))}
                />
              </div>
              <div className="col-lg-3 mb-3 pa-required">
                <label htmlFor="pa-holding-reserves" className="form-label">
                  Holding Reserves %
                </label>
                <input
                  id="pa-holding-reserves"
                  type="number"
                  step="any"
                  className="form-control"
                  value={Number.isNaN(form.holdingReserves) ? '' : form.holdingReserves}
                  onChange={(e) => patch({ holdingReserves: toNumber(e.target.value) })}
                />
              </div>
              <div className="col-lg-3 mb-3 pa-required">
                <label htmlFor="pa-rolling-days" className="form-label">
                  Rolling Days (X)
                </label>
                <input
                  id="pa-rolling-days"
                  type="number"
                  className="form-control"
                  value={Number.isNaN(form.rollingDays) ? '' : form.rollingDays}
                  onChange={(e) => patch({ rollingDays: Math.trunc(toNumber(e.target.value)) })}
                />
              </div>
              <div className="col-lg-3 mb-3">
                <DateRangePicker
                  mode="single"
                  label="Matching Starting Date"
                  value={form.providerStatementStartingDate || null}
                  onChange={(v) => patch({ providerStatementStartingDate: v })}
                />
              </div>
              <div className="col-lg-3 mb-3 pa-required">
                <Select2
                  label="Provider Time Zone"
                  placeholder="Select Time Zone"
                  value={form.providerTimeZone}
                  onChange={(v) => patch({ providerTimeZone: v ?? '' })}
                  options={lookup.timeZones.map((tz) => ({ value: tz, label: tz }))}
                />
              </div>
              <div className="col-lg-3 mb-3 pa-required">
                <label htmlFor="pa-min-match" className="form-label">
                  Minimum Matching Percentage
                </label>
                <input
                  id="pa-min-match"
                  type="number"
                  step="any"
                  className="form-control"
                  value={Number.isNaN(form.minMatchPercentage) ? '' : form.minMatchPercentage}
                  onChange={(e) => patch({ minMatchPercentage: toNumber(e.target.value) })}
                />
              </div>
              <div className="col-lg-3 mb-3 pa-required">
                <Select2
                  label="Ownership"
                  placeholder="Select Ownership"
                  value={form.ownership}
                  onChange={(v) => patch({ ownership: v ?? '' })}
                  options={OWNERSHIP_OPTIONS.map((o) => ({ value: o, label: o }))}
                />
              </div>

              {!isNew && (
                <>
                  <div className="col-lg-3 mb-3 pa-required">
                    <label htmlFor="pa-sim-date" className="form-label">
                      Date Similarity Percentage (%)
                    </label>
                    <input
                      id="pa-sim-date"
                      type="number"
                      step="any"
                      className="form-control"
                      value={Number.isNaN(form.dateSimilarityPercentage) ? '' : form.dateSimilarityPercentage}
                      onChange={(e) => patch({ dateSimilarityPercentage: toNumber(e.target.value) })}
                    />
                  </div>
                  <div className="col-lg-3 mb-3 pa-required">
                    <label htmlFor="pa-sim-amount" className="form-label">
                      Amount Similarity Percentage (%)
                    </label>
                    <input
                      id="pa-sim-amount"
                      type="number"
                      step="any"
                      className="form-control"
                      value={Number.isNaN(form.amountSimilarityPercentage) ? '' : form.amountSimilarityPercentage}
                      onChange={(e) => patch({ amountSimilarityPercentage: toNumber(e.target.value) })}
                    />
                  </div>
                  <div className="col-lg-3 mb-3 pa-required">
                    <label htmlFor="pa-sim-provider" className="form-label">
                      Provider Name Similarity Percentage (%)
                    </label>
                    <input
                      id="pa-sim-provider"
                      type="number"
                      step="any"
                      className="form-control"
                      value={
                        Number.isNaN(form.providerNameSimilarityPercentage)
                          ? ''
                          : form.providerNameSimilarityPercentage
                      }
                      onChange={(e) => patch({ providerNameSimilarityPercentage: toNumber(e.target.value) })}
                    />
                  </div>
                  <div className="col-lg-3 mb-3 pa-required">
                    <label htmlFor="pa-sim-country" className="form-label">
                      Country Similarity Percentage (%)
                    </label>
                    <input
                      id="pa-sim-country"
                      type="number"
                      step="any"
                      className="form-control"
                      value={Number.isNaN(form.countrySimilarityPercentage) ? '' : form.countrySimilarityPercentage}
                      onChange={(e) => patch({ countrySimilarityPercentage: toNumber(e.target.value) })}
                    />
                  </div>
                  <div className="col-lg-3 mb-3 pa-required">
                    <label htmlFor="pa-transaction-plus-x" className="form-label">
                      Transaction + X Days
                    </label>
                    <input
                      id="pa-transaction-plus-x"
                      type="number"
                      min={0}
                      className="form-control"
                      value={Number.isNaN(form.transactionPlusXDays) ? '' : form.transactionPlusXDays}
                      onChange={(e) => patch({ transactionPlusXDays: Math.trunc(toNumber(e.target.value)) })}
                    />
                  </div>
                  <div className="col-lg-3 mb-3 pa-required">
                    <Select2Multi
                      label="Connected Client Bank Account"
                      value={form.linkedProviderAccountIds}
                      onChange={(v) => patch({ linkedProviderAccountIds: v })}
                      options={linkableAccounts.map((l) => ({ value: l.id, label: l.name }))}
                    />
                  </div>
                </>
              )}

              <div className="col-lg-3 mb-3 pa-required">
                <Select2
                  label="Account Type"
                  allowClear={false}
                  value={form.accountType}
                  onChange={(v) => patch({ accountType: v ? Number(v) : 0 })}
                  options={ACCOUNT_TYPE_OPTIONS}
                />
              </div>

              <div className="col-lg-1 mb-3 mt-auto">
                <label htmlFor="pa-process-d1" className="form-label d-block">
                  Process D - 1
                </label>
                <div className="form-check form-switch">
                  <input
                    id="pa-process-d1"
                    type="checkbox"
                    className="form-check-input"
                    checked={form.processDayMinus1}
                    onChange={(e) => patch({ processDayMinus1: e.target.checked })}
                  />
                </div>
              </div>
              <div className="col-lg-2 mb-3 mt-auto">
                <label htmlFor="pa-exclude" className="form-label d-block">
                  Exclude from Provider Assets
                </label>
                <div className="form-check form-switch">
                  <input
                    id="pa-exclude"
                    type="checkbox"
                    className="form-check-input"
                    checked={form.excludeFromProviderAssets}
                    onChange={(e) => patch({ excludeFromProviderAssets: e.target.checked })}
                  />
                </div>
              </div>
              <div className="col-lg-3 mb-3 mt-auto">
                <label htmlFor="pa-include-statements" className="form-label d-block">
                  Include Statements In Provider Assets
                </label>
                <div className="form-check form-switch">
                  <input
                    id="pa-include-statements"
                    type="checkbox"
                    className="form-check-input"
                    checked={form.includeStatementsInProviderAssets}
                    onChange={(e) => patch({ includeStatementsInProviderAssets: e.target.checked })}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* _FeesType.cshtml */}
        <div className="card custom-card">
          <div className="card-header border-bottom d-flex justify-content-between align-items-center">
            <h3 className="card-title mb-0">Fees Type</h3>
            <div className="col-lg-3 ms-4 mb-0">
              <Select2
               
                allowClear={false}
                value={form.feesCondition}
                onChange={(v) => patch({ feesCondition: (v ? Number(v) : ConditionalFee.WithoutConditions) as ConditionalFee })}
                options={FEES_CONDITION_OPTIONS}
              />
            </div>
          </div>
          <div className="card-body">
            {withConditions ? (
              <div id="pa-fees-with" className="mt-3">
                <div className="row">
                  <div className="col-lg-4">
                    <div className="card shadow-none border mb-3">
                      <div className="card-body">
                        <div className="row align-items-center mb-3">
                          <div className="col-auto">
                            <label className="form-label mb-0">
                              If Amount &lt; <span className="text-danger">*</span>
                            </label>
                          </div>
                          <div className="col">
                            <input
                              id="pa-threshold-min"
                              type="number"
                              step="any"
                              className={`form-control${thresholdInvalid ? ' is-invalid' : ''}`}
                              placeholder="Threshold"
                              value={form.minThreshold}
                              onChange={(e) => patch({ minThreshold: e.target.value })}
                            />
                          </div>
                        </div>
                        <FeeInputs
                          idPrefix="pa-lt"
                          value={form.lessThanFees}
                          onChange={(next) => patch({ lessThanFees: next })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="card shadow-none border mb-3">
                      <div className="card-body">
                        <div className="row align-items-center mb-3">
                          <div className="col-auto">
                            <label className="form-label mb-0">
                              If Amount between <span className="text-danger">*</span>
                            </label>
                          </div>
                          <div className="col">
                            <input
                              id="pa-threshold-min-display"
                              type="text"
                              className="form-control"
                              placeholder="Min"
                              value={formatThresholdDisplay(form.minThreshold)}
                              disabled
                            />
                          </div>
                          <div className="col-auto">
                            <label className="form-label mb-0">and</label>
                          </div>
                          <div className="col">
                            <input
                              id="pa-threshold-max-display"
                              type="text"
                              className="form-control"
                              placeholder="Max"
                              value={formatThresholdDisplay(form.maxThreshold)}
                              disabled
                            />
                          </div>
                        </div>
                        <FeeInputs
                          idPrefix="pa-bt"
                          value={form.betweenFees}
                          onChange={(next) => patch({ betweenFees: next })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="card shadow-none border mb-3">
                      <div className="card-body">
                        <div className="row align-items-center mb-3">
                          <div className="col-auto">
                            <label className="form-label mb-0">
                              If Amount &gt; <span className="text-danger">*</span>
                            </label>
                          </div>
                          <div className="col">
                            <input
                              id="pa-threshold-max"
                              type="number"
                              step="any"
                              className={`form-control${thresholdInvalid ? ' is-invalid' : ''}`}
                              placeholder="Threshold"
                              value={form.maxThreshold}
                              onChange={(e) => patch({ maxThreshold: e.target.value })}
                            />
                          </div>
                        </div>
                        <FeeInputs
                          idPrefix="pa-gt"
                          value={form.greaterThanFees}
                          onChange={(next) => patch({ greaterThanFees: next })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {thresholdInvalid && (
                  <div id="pa-threshold-error" className="text-danger small mt-2">
                    The &quot;If Amount &lt;&quot; threshold must be less than or equal to the &quot;If Amount
                    &gt;&quot; threshold.
                  </div>
                )}
              </div>
            ) : (
              <div id="pa-fees-without" className="row mt-3">
                <FeeInputs
                  idPrefix="pa-w"
                  wrapperClass="col-lg-3 mb-3"
                  value={form.feesWithoutConditions}
                  onChange={(next) => patch({ feesWithoutConditions: next })}
                />
              </div>
            )}
          </div>
        </div>

        <div className="text-end mb-4">
          <a className="btn btn-dark me-2" href="/admin/providers">
            Cancel
          </a>
          <button
            type="button"
            className="btn btn-success"
            id="pa-save-account"
            disabled={saving || thresholdInvalid}
            onClick={handleSave}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                Saving...
              </>
            ) : isNew ? (
              <>
                <i className="fa fa-plus me-1" />
                Add Provider
              </>
            ) : (
              'Update'
            )}
          </button>
        </div>

        {!isNew && account && <AccountLogsGrids providerAccountId={account.id} />}
      </form>
    </div>
  );
}
